import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { useMarketOptions } from '@/hooks/discover/useMarketOptions';
import { push } from 'redux-first-router';
import { useTheme, Grid } from '@simplywallst/ui-core';
import { getPath } from '@/router/utils';
import type { PrimaryCountryISO } from '@/constants/countries';
import type { ROUTE_MARKET_SECTOR } from '@/constants/routes';
import { ROUTE_MARKET } from '@/constants/routes';
import { ExtraWrapper, Link } from './styled';
import { MinimalDropdown } from '@/components/Dropdown';
import type { Option } from '@/components/Dropdown/components/Options';
import useIndustryOptions from '@/pages/Market/hooks/useIndustryOptions';
import useReduxStateMarket from '@/pages/Market/hooks/useReduxStateMarkets';
import useWindowSize from '@/hooks/useWindowSize';
import { getFlagCharacter } from '@/utilities/getFlagCharacter';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

interface PathRequest {
  type: typeof ROUTE_MARKET | typeof ROUTE_MARKET_SECTOR;
  payload: {
    market: PrimaryCountryISO;
    industry?: string | 'any';
  };
}

const defaultMarkets: Array<PrimaryCountryISO> = ['us', 'au', 'ca', 'in', 'za'];

export const Filters = () => {
  const { market, pathname: industryDropdownValue } = useReduxStateMarket();

  const theme = useTheme();
  const { trackEvent } = useTracking();
  const { width: screenWidth = 0 } = useWindowSize();
  const { isDesktop } = useUserAgent();
  const { os } = useUserAgent();
  const isLargeScreen =
    screenWidth === 0 ? isDesktop : screenWidth > theme.mdMinRaw;

  const marketOptions = useMarketOptions(false, ROUTE_MARKET);
  const { options: industryOptions = [], trendingIndustries = [] } =
    useIndustryOptions(true, market);

  const onMarketChanged = useCallback((market: string) => {
    const pathRequest: PathRequest = {
      type: ROUTE_MARKET,
      payload: {
        market: market as PrimaryCountryISO,
      },
    };
    try {
      trackEvent({
        type: 'track',
        subject: 'market_dropdown',
        action: 'click',
        data: market,
      });
      push(getPath(pathRequest));
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const TrendingIndustries = useCallback(
    () =>
      trendingIndustries.length > 0 ? (
        <ExtraWrapper
          alignItems="center"
          templateColumns="repeat(6, max-content)"
          columnGap="8px"
        >
          <span>Trending </span>
          {trendingIndustries.map((i) => (
            <Link
              key={i.id}
              styleType="passiveReverse"
              to={i.href}
              onClick={() => {
                trackEvent({
                  type: 'track',
                  subject: 'trending_industries',
                  action: 'click',
                  data: i.label,
                });
              }}
            >
              <span>{i.label}</span>
            </Link>
          ))}
        </ExtraWrapper>
      ) : (
        <div />
      ),
    [trendingIndustries]
  );

  const countryFlag = os.toLowerCase().includes('windows')
    ? ''
    : getFlagCharacter(market);

  const TopMarkets = useCallback(() => {
    const topMarkets = [market, ...defaultMarkets.filter((m) => m !== market)];
    return (
      <ExtraWrapper
        alignItems="center"
        templateColumns="repeat(6, max-content)"
        columnGap="8px"
      >
        {topMarkets.map((m, index) => (
          <Link
            key={m}
            styleType="passiveReverse"
            onClick={() => {
              trackEvent({
                type: 'track',
                subject: 'top_markets',
                action: 'click',
                data: m,
              });
            }}
            to={getPath({
              type: ROUTE_MARKET,
              payload: {
                market: m,
              },
            })}
          >
            <span>
              {index === 0 ? `${countryFlag}\u00A0 ` : ''}
              {m.toUpperCase()}
            </span>
          </Link>
        ))}
      </ExtraWrapper>
    );
  }, [market, countryFlag]);

  const onIndustryChanged = useCallback((value: string) => {
    try {
      trackEvent({
        type: 'track',
        subject: 'industry_dropdown',
        action: 'click',
        data: value,
      });
      push(value);
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const marketLabel = `${countryFlag}\u00A0 ${market.toUpperCase()} ${
    isLargeScreen ? 'Market' : ''
  }`;

  const industryLabel = (): string => {
    if (industryOptions && industryOptions.length > 0) {
      const currentIndustry = industryOptions.find(
        (i) => i.option === industryDropdownValue
      );
      if (currentIndustry && currentIndustry.label !== 'All Industries') {
        return currentIndustry.label;
      }
    }
    return 'Industry';
  };

  return (
    <Grid templateColumns="auto auto" columnGap="8px">
      <MinimalDropdown
        displayType="inline-block"
        canSearch
        name="market-page-market-dropdown"
        options={marketOptions}
        onSelect={onMarketChanged}
        value={market}
        autoComplete="off"
        placeholder="Search e.g. United States"
        label={marketLabel}
        extraToggle={() => <TopMarkets />}
      />
      <MinimalDropdown
        displayType="inline-block"
        canSearch
        name="market-page-industry-dropdown"
        options={industryOptions as Option[]}
        onSelect={onIndustryChanged}
        value={industryDropdownValue}
        autoComplete="off"
        placeholder="Search e.g. oil, banks..."
        label={industryLabel()}
        width={320}
        underline={false}
        extraToggle={() => <TrendingIndustries />}
      />
    </Grid>
  );
};
